import type {LatLngBounds} from 'leaflet';
import * as L from 'leaflet';
import proj4 from 'proj4';

import type {IFeature, IWSFeatureItem} from '@/api/types/layer/feature';

import type {
  Feature,
  Geometry,
  MultiPolygon,
  Polygon,
  Position,
} from '@turf/turf';
import {feature, polygon} from '@turf/turf';

proj4.defs('EPSG:7683', '+proj=longlat +ellps=GSK2011 +no_defs +type=crs');
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs +type=crs');

export const parsePolygonFromBounds = (bounds?: LatLngBounds) => {
  if (!bounds) return undefined;

  return polygon([
    [
      [bounds.getEast(), bounds.getSouth()],
      [bounds.getWest(), bounds.getSouth()],
      [bounds.getWest(), bounds.getNorth()],
      [bounds.getEast(), bounds.getNorth()],
      [bounds.getEast(), bounds.getSouth()],
    ],
  ]);
};

export const projectCoordsTo3857 = (coords: Position): Position => {
  const point = L.CRS.EPSG3857.project({lng: coords[0], lat: coords[1]});
  return [point.x, point.y];
};

export const projectCoordsTo4326 = (coords: Position): Position => {
  const point = L.CRS.EPSG4326.project({lng: coords[0], lat: coords[1]});
  return [point.x, point.y];
};

export const projectCoordsTo7683 = (coords: Position): Position => {
  const point = proj4('EPSG:4326', 'EPSG:7683', {x: coords[0], y: coords[1]});
  return [point.x, point.y];
};

export const projectCoords = (
  coords: Position | Position[] | Position[][] | Position[][][],
  projector: (coords: Position) => Position,
): Position | Position[] | Position[][] | Position[][][] => {
  if (
    coords.length === 2 &&
    typeof coords[0] === 'number' &&
    typeof coords[1] === 'number'
  ) {
    const parsedCoords = coords as Position;
    return projector(parsedCoords);
  }

  const nestedCoords: unknown[] = [];

  coords.forEach((item) => {
    if (Array.isArray(item)) {
      nestedCoords.push(projectCoords(item, projector));
    }
  });

  return nestedCoords as Position | Position[] | Position[][] | Position[][][];
};

export const parseWktFromCoords = (
  coords?: Position | Position[] | Position[][] | Position[][][],
): string | undefined => {
  if (!coords) return undefined;

  if (
    coords.length === 2 &&
    typeof coords[0] === 'number' &&
    typeof coords[1] === 'number'
  ) {
    const parsedCoords = coords as Position;
    return `${parsedCoords[0]} ${parsedCoords[1]}`;
  }

  const points: string[] = [];

  coords.forEach((item) => {
    if (Array.isArray(item)) points.push(parseWktFromCoords(item) ?? '');
  });

  return `(${points.join(', ')})`;
};

export const serializeGeometryToWkt = (geometry: {
  coordinates: Position | Position[] | Position[][] | Position[][][];
  type: string;
}) => {
  if (!('coordinates' in geometry)) return '';
  const type = geometry.type.toUpperCase();
  let coordinates = parseWktFromCoords(geometry.coordinates);
  coordinates = type === 'POINT' ? `(${coordinates})` : coordinates;
  return `${type}${coordinates}`;
};

export const parseWktFromFeature = (
  feature?: Feature<Polygon | MultiPolygon>,
): string | undefined => {
  if (!feature || !feature.geometry) return undefined;
  return `${feature.geometry.type.toUpperCase()}${parseWktFromCoords(feature.geometry.coordinates)}`;
};

export const serializeGeometryToGeoJSON = (geometry: {
  coordinates: Position | Position[] | Position[][] | Position[][][];
  type: string;
}) => {
  return JSON.stringify(geometry, null, 4);
};

export const parseFeatureFromIFeature = (
  val: IFeature | (IWSFeatureItem & Required<Pick<IWSFeatureItem, 'geom'>>),
): Feature<Geometry> => {
  return feature(val.geom, {...(val.fields ?? {}), __id__: val.id});
};
